/**
 * Statistics: the overview, the drill-down screens (pages, referrers,
 * geography, devices, campaigns, conversions, errors) and the analytics
 * components and charts they share.
 */
export default {
    shared: {
        views: 'Views',
        uniqueVisitors: 'Unique visitors',
        uniques: 'Uniques',
        sessions: 'Sessions',
        avgDwell: 'Avg dwell',
        botViews: 'Bot views',
        bounce: 'Bounce',
        path: 'Path',
        source: 'Source',
        orders: 'Orders',
        revenue: 'Revenue',
        conversionRate: 'Conversion rate',
        formSubmissions: 'Leads',
        clientErrors: 'Client errors (4xx)',
        serverErrors: 'Server errors (5xx)',
        redirects: 'Redirects (3xx)',
        direct: '(direct)',
        none: '(none)',
        unknown: '(unknown)',
        viewAll: 'View all',
        noDataYet: 'No data yet',
        noDataRange: 'No data for this range.',
    },
    units: {
        milliseconds: '{n} ms',
        seconds: '{n} s',
        minutesSeconds: '{m}m {s}s',
    },
    nav: {
        label: 'Statistics',
        overview: 'Overview',
        pages: 'Pages',
        referrers: 'Referrers',
        geo: 'Geography',
        technology: 'Devices',
        campaigns: 'Campaigns',
        conversions: 'Conversions',
        errors: 'Errors',
    },
    range: {
        preset: '{n}d',
    },
    change: {
        new: 'New',
        noChange: 'No change',
        vsPrevious: 'vs previous day | vs previous {n} days',
        previous: 'Previous day: {value} | Previous {n} days: {value}',
    },
    attribution: {
        viewsTitle: 'All views in these sessions',
        bounceTitle: 'Sessions with a single view',
        convRate: 'Conv. rate',
        rateOrders: 'Paid orders per session',
        rateForms: 'Leads (forms, calls, WhatsApp, e-mail) per session',
        revenueTitle: 'Paid orders, refunds excluded',
        forms: 'Leads',
    },
    conversionTiles: {
        notApplicable: 'n/a',
        ofSessions: '{rate} of sessions',
    },
    conversionChart: {
        revenue: 'Revenue: {amount}',
    },
    shareList: {
        tooltip: '{visitors} unique visitors, {sessions} sessions',
    },
    worldMap: {
        label: 'Visitors by country',
        hover: '{views} views · {uniques} uniques',
        noVisitorsRange: 'No visitors in this range',
        noVisitors: 'No visitors',
        unmapped: 'Too small to draw at this scale, see the table: {countries}',
    },
    overview: {
        title: 'Statistics',
        botTraffic: 'Bot traffic',
        transferred: 'Transferred',
        trafficComparison: 'Traffic comparison',
        dailyTrend: 'Daily trend',
        compare: 'Compare',
        dailyBreakdown: 'Daily breakdown',
        day: 'Day',
        topPages: 'Top pages',
        topReferrers: 'Top referrers',
        topCountries: 'Top countries',
        topErrors: 'Top non-2xx pages',
        bounce: '{pct} bounce',
    },
    pages: {
        title: 'Top pages',
        entryHeading: 'Where sessions start',
        pagesHeading: 'Pages by views',
        allPages: 'All pages',
        entryPages: 'Entry pages',
        entryPage: 'Entry page',
        entries: 'Entries',
        avgScroll: 'Avg scroll',
        avgScrollTitle: 'How far down the page visitors scrolled, on average',
    },
    referrers: {
        title: 'Top referrers',
        referrers: 'Referrers',
        referrerHost: 'Referrer host',
        outbound: 'Outbound links',
        destinationHost: 'Destination host',
        clicks: 'Clicks',
        noOutbound: 'No outbound clicks in this range.',
    },
    geo: {
        title: 'Geography',
        whereVisitorsAre: 'Where visitors are',
        countries: 'Countries',
        cities: 'Cities',
        location: 'Location',
        countryOnly: '(country only)',
    },
    technology: {
        title: 'Devices',
        devices: 'Devices',
        browsers: 'Browsers',
        os: 'Operating systems',
        languages: 'Site languages',
        languagesHint: "The language version of the site that was visited, not the browser's language.",
        device: {
            desktop: 'Desktop',
            smartphone: 'Smartphone',
            tablet: 'Tablet',
            phablet: 'Phablet',
            featurePhone: 'Feature phone',
            console: 'Console',
            tv: 'TV',
            carBrowser: 'Car browser',
            smartDisplay: 'Smart display',
            smartSpeaker: 'Smart speaker',
            camera: 'Camera',
            mediaPlayer: 'Portable media player',
            wearable: 'Wearable',
            peripheral: 'Peripheral',
        },
    },
    campaigns: {
        title: 'Campaigns',
        empty: 'No campaign traffic in this range.',
        emptyHint: 'Add UTM parameters to the links you share, for example {example}. Visits that arrive through such a link are grouped here by source, medium and campaign.',
        intro: 'Sessions are grouped by the UTM parameters on the page they landed on. "{none}" is every session whose landing page had no such parameter.',
        sources: 'Sources',
        mediums: 'Mediums',
        campaigns: 'Campaigns',
        medium: 'Medium',
        campaign: 'Campaign',
    },
    conversions: {
        title: 'Conversions',
        ordersNote: 'Orders count once paid; refunded orders are left out. Revenue is the order total.',
        formsNote: 'A lead is a form submission, counted when it was stored, or a tap on the phone number, e-mail address or WhatsApp button. Each one is credited to the traffic source and landing page of the visit it came from.',
        daily: 'Daily conversions',
        bySource: 'By traffic source',
        byLandingPage: 'By landing page',
        byForm: 'By form or contact button',
        landingPage: 'Landing page',
        form: 'Form or button',
        submissions: 'Leads',
        unnamed: '(unnamed)',
        noForms: 'No leads in this range.',
        contact: {
            phone: 'Phone number tapped',
            email: 'E-mail address tapped',
            whatsapp: 'WhatsApp tapped',
        },
    },
    errors: {
        title: 'Errors',
        overTime: 'Status codes over time',
        byStatus: 'Pages by status',
        classOnly: '{label} only',
        showAll: 'Show all',
        hits: 'Hits',
        noneOfClass: 'No {label} responses in this range.',
        noneAtAll: 'No redirects or errors in this range. 🎉',
    },
};
